/**
 * Exam Corner — curated links to OFFICIAL, free exam resources.
 *
 * We LINK OUT (new tab); we never host or reproduce the material — so there is
 * no copyright issue, and students reach the genuine first-hand source. All
 * descriptions are original. URLs verified official + live (June 2026) by a
 * research pass. Some official pages (syllabus, sample papers, focus areas)
 * rotate each academic year, so those carry a `note` and we word them as the
 * "current session" rather than a frozen file. Only government / board domains
 * are used — never third-party aggregators (hsslive, learncbse, etc.).
 */
export interface ExamResource {
  category: string;
  title: string;
  description: string;
  url: string;
  /** Display label for the official domain, e.g. "ncert.nic.in". */
  source: string;
  appliesTo: string;
  /** Flags resources whose official URL changes each academic year. */
  note?: string;
}

export interface ExamBoard {
  id: string;
  label: string;
  blurb: string;
  resources: ExamResource[];
  footnote?: string;
}

export const examBoards: ExamBoard[] = [
  {
    id: 'cbse',
    label: 'CBSE & NCERT (National)',
    blurb: 'Everything here is published free by NCERT and the CBSE Academic Unit — download straight from the source.',
    resources: [
      {
        category: 'Free textbooks',
        title: 'NCERT textbooks — Accountancy, Business Studies & Economics',
        description: 'The complete Class 11 & 12 NCERT books as free official PDFs. Pick your class and subject, then download chapter-wise or the whole book.',
        url: 'https://ncert.nic.in/textbook.php',
        source: 'ncert.nic.in',
        appliesTo: 'Class 11 & 12',
      },
      {
        category: 'Free textbooks',
        title: 'ePathshala — NCERT books online',
        description: 'The same NCERT books to read online or download as PDF / ePub, from the Ministry of Education’s official platform.',
        url: 'https://epathshala.nic.in/process.php?id=students&type=eTextbooks&ln=en',
        source: 'epathshala.nic.in',
        appliesTo: 'Class 11 & 12',
      },
      {
        category: 'Syllabus',
        title: 'CBSE commerce syllabus (current session)',
        description: 'The exact official syllabus for each commerce subject — what’s included and what’s been trimmed this year. Read it before you start a chapter.',
        url: 'https://cbseacademic.nic.in/curriculum_2027.html',
        source: 'cbseacademic.nic.in',
        appliesTo: 'Class 11 & 12',
        note: 'CBSE issues a fresh syllabus each year (this is the 2026-27 session). Open the latest one listed on the page.',
      },
      {
        category: 'Sample papers',
        title: 'CBSE Class 12 sample papers + marking schemes',
        description: 'Official Class 12 sample question papers with their marking schemes — the closest practice you’ll get to the real board paper.',
        url: 'https://cbseacademic.nic.in/sqp_classxii_2025-26.html',
        source: 'cbseacademic.nic.in',
        appliesTo: 'Class 12',
        note: 'A new set is released each session (this is 2025-26). For the latest, see the CBSE Academic sample-paper page.',
      },
      {
        category: 'Previous papers',
        title: 'CBSE previous-year board question papers',
        description: 'Actual past board exam papers for recent years — free, straight from CBSE’s own website.',
        url: 'https://www.cbse.gov.in/cbsenew/question-paper.html',
        source: 'cbse.gov.in',
        appliesTo: 'Class 12',
      },
    ],
    footnote: 'Class 11 is a school-level exam, not a CBSE board exam — so CBSE doesn’t publish Class 11 sample or previous-year papers. For Class 11, rely on the NCERT textbooks and the official syllabus above. (Any site offering a “CBSE Class 11 board paper” is unofficial.)',
  },
  {
    id: 'kerala',
    label: 'Kerala State (SCERT / DHSE)',
    blurb: 'Free Kerala Higher Secondary resources from SCERT and the Directorate of Higher Secondary Education (DHSE).',
    resources: [
      {
        category: 'Free textbooks',
        title: 'SCERT Plus One & Plus Two commerce textbooks',
        description: 'Official Kerala state textbooks (English & Malayalam) for Accountancy, Business Studies and Economics — free from the SCERT / KITE Samagra portal.',
        url: 'https://samagra.kite.kerala.gov.in/',
        source: 'samagra.kite.kerala.gov.in',
        appliesTo: 'Plus One & Plus Two',
      },
      {
        category: 'Syllabus & curriculum',
        title: 'SCERT Kerala — syllabus & curriculum',
        description: 'The state curriculum and Higher Secondary syllabus documents, from Kerala’s official textbook and curriculum authority.',
        url: 'https://scert.kerala.gov.in/',
        source: 'scert.kerala.gov.in',
        appliesTo: 'Plus One & Plus Two',
      },
      {
        category: 'Model & previous papers',
        title: 'DHSE model & previous question papers',
        description: 'Model / sample question papers and past papers for Plus One & Plus Two — from the official DHSE site (open the “Sample Question Papers” section).',
        url: 'http://www.dhsekerala.gov.in/',
        source: 'dhsekerala.gov.in',
        appliesTo: 'Plus One & Plus Two',
        note: 'DHSE posts the current year’s set and rotates the links each cycle — navigate from the homepage rather than bookmarking a single file.',
      },
      {
        category: 'Focus area',
        title: 'Kerala HSE Focus Area (reduced portion)',
        description: 'The “focus area” portion DHSE emphasises for the public exam, released as an official circular before each exam season.',
        url: 'http://www.dhsekerala.gov.in/',
        source: 'dhsekerala.gov.in',
        appliesTo: 'Plus One & Plus Two',
        note: 'Published yearly via DHSE circulars (Downloads → Circulars) — there’s no permanent link, so look for the current year’s notification.',
      },
    ],
  },
];
